// src/pages/Reports.tsx
// Plezyr Admin - User Reports Management
import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';

interface ReportUser {
  id: number;
  name: string;
  email: string | null;
  avatar_url: string | null;
}

interface Report {
  id: number;
  reporter: ReportUser;
  reported: ReportUser;
  reason: string;
  description: string | null;
  status: 'pending' | 'reviewed' | 'resolved';
  created_at: string;
  reviewed_at: string | null;
}

interface ReportsResponse {
  success: boolean;
  reports: Report[];
  total: number;
  skip: number;
  limit: number;
}

// Helper to extract error message from API response
const extractErrorMessage = (err: any): string => {
  const detail = err.response?.data?.detail;
  if (!detail) return 'Une erreur est survenue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return JSON.stringify(detail);
};

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useNotification();

  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('all');

  // Dialog state
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'view' | 'warn' | 'ban' | 'resolve';
  }>({ open: false, type: 'view' });
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    resolved: 0,
  });

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (reasonFilter !== 'all') params.reason = reasonFilter;

      const response = await api.get<ReportsResponse>('/admin/reports', { params });
      setReports(response.data.reports);

      // Fetch stats
      const statsResponse = await api.get('/admin/stats/reports');
      if (statsResponse.data.success) {
        setStats({
          total: statsResponse.data.total,
          pending: statsResponse.data.by_status.pending,
          reviewed: statsResponse.data.by_status.reviewed,
          resolved: statsResponse.data.by_status.resolved,
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [statusFilter, reasonFilter]);

  const handleUpdateStatus = async (reportId: number, newStatus: string) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/reports/${reportId}`, { status: newStatus });
      success(`Signalement mis à jour: ${newStatus}`);
      fetchReports();
      setActionDialog({ open: false, type: 'view' });
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async (reportId: number, action: 'warn' | 'ban') => {
    try {
      setActionLoading(true);
      await api.post(`/admin/reports/${reportId}/action`, { action });
      success(action === 'ban' ? 'Utilisateur banni' : 'Avertissement envoyé');
      fetchReports();
      setActionDialog({ open: false, type: 'view' });
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const statusChip = (status: Report['status']) => {
    switch (status) {
      case 'resolved':
        return <Chip label="Résolu" color="success" size="small" />;
      case 'reviewed':
        return <Chip label="Examiné" color="info" size="small" />;
      default:
        return <Chip label="En attente" color="warning" size="small" />;
    }
  };

  const reasonChip = (reason: string) => {
    const colors: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
      harassment: 'error',
      fake: 'warning',
      inappropriate: 'error',
      spam: 'info',
      other: 'default',
    };
    const labels: Record<string, string> = {
      harassment: 'Harcèlement',
      fake: 'Faux profil',
      inappropriate: 'Inapproprié',
      spam: 'Spam',
      other: 'Autre',
    };
    return (
      <Chip
        label={labels[reason] || reason}
        color={colors[reason] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const filteredReports = reports.filter((report) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        report.reporter.name.toLowerCase().includes(search) ||
        report.reported.name.toLowerCase().includes(search) ||
        report.reason.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('reports.title', 'Signalements')}
        </Typography>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={fetchReports}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#f8fafc' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700}>{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#fef3c7' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">{stats.pending}</Typography>
              <Typography variant="caption" color="text.secondary">En attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#dbeafe' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="info.main">{stats.reviewed}</Typography>
              <Typography variant="caption" color="text.secondary">Examinés</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: '#d1fae5' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">{stats.resolved}</Typography>
              <Typography variant="caption" color="text.secondary">Résolus</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={statusFilter}
            label="Statut"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="reviewed">Examiné</MenuItem>
            <MenuItem value="resolved">Résolu</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Raison</InputLabel>
          <Select
            value={reasonFilter}
            label="Raison"
            onChange={(e) => setReasonFilter(e.target.value)}
          >
            <MenuItem value="all">Toutes</MenuItem>
            <MenuItem value="fake">Faux profil</MenuItem>
            <MenuItem value="harassment">Harcèlement</MenuItem>
            <MenuItem value="inappropriate">Inapproprié</MenuItem>
            <MenuItem value="spam">Spam</MenuItem>
            <MenuItem value="other">Autre</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Reports Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell>Signalé</TableCell>
              <TableCell>Par</TableCell>
              <TableCell>Raison</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Aucun signalement trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredReports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        src={report.reported.avatar_url || undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {report.reported.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {report.reported.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {report.reported.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{report.reporter.name}</Typography>
                  </TableCell>
                  <TableCell>{reasonChip(report.reason)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(report.created_at).toLocaleDateString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell>{statusChip(report.status)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Voir détails">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedReport(report);
                          setActionDialog({ open: true, type: 'view' });
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {report.status === 'pending' && (
                      <>
                        <Tooltip title="Marquer examiné">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleUpdateStatus(report.id, 'reviewed')}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bannir l'utilisateur">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedReport(report);
                              setActionDialog({ open: true, type: 'ban' });
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: 'view' })}
        maxWidth="sm"
        fullWidth
      >
        {selectedReport && (
          <>
            <DialogTitle>
              {actionDialog.type === 'ban'
                ? `Bannir ${selectedReport.reported.name} ?`
                : `Signalement #${selectedReport.id}`}
            </DialogTitle>
            <DialogContent>
              {actionDialog.type === 'view' ? (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Utilisateur signalé
                    </Typography>
                    <Typography>{selectedReport.reported.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Signalé par
                    </Typography>
                    <Typography>{selectedReport.reporter.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Raison
                    </Typography>
                    {reasonChip(selectedReport.reason)}
                  </Box>
                  {selectedReport.description && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography>{selectedReport.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography>
                      {new Date(selectedReport.created_at).toLocaleString('fr-FR')}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <DialogContentText>
                  Cette action va bannir l'utilisateur <strong>{selectedReport.reported.name}</strong> et marquer le signalement comme résolu.
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setActionDialog({ open: false, type: 'view' })}>
                Fermer
              </Button>
              {actionDialog.type === 'view' && selectedReport.status !== 'resolved' && (
                <>
                  <Button
                    color="warning"
                    onClick={() => handleAction(selectedReport.id, 'warn')}
                    disabled={actionLoading}
                  >
                    Avertir
                  </Button>
                  <Button
                    color="error"
                    onClick={() => handleAction(selectedReport.id, 'ban')}
                    disabled={actionLoading}
                  >
                    Bannir
                  </Button>
                  <Button
                    color="success"
                    onClick={() => handleUpdateStatus(selectedReport.id, 'resolved')}
                    disabled={actionLoading}
                  >
                    Résoudre
                  </Button>
                </>
              )}
              {actionDialog.type === 'ban' && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => handleAction(selectedReport.id, 'ban')}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Confirmer le ban'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// Grid component for stats
const Grid = ({ container, item, children, ...props }: any) => {
  if (container) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1, ...props.sx }}>
        {children}
      </Box>
    );
  }
  const { xs, sm, md, lg } = props;
  const getWidth = () => {
    // Simplified responsive width calculation
    const cols = md || sm || xs || 12;
    return `${(cols / 12) * 100}%`;
  };
  return (
    <Box sx={{ width: getWidth(), padding: 1 }}>
      {children}
    </Box>
  );
};

export default Reports;
